import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { authService, ApiError, IdType } from '../services';
import { useRecaptcha } from '../hooks/useRecaptcha';
import SEO from '../components/SEO';
import SEOErrorBoundary from '../components/SEOErrorBoundary';
import useStructuredData from '../hooks/useStructuredData';
import { DEFAULT_LANGUAGE } from '../utils/languageUtils';

interface FormData {
  nationalId: string;
  email: string;
  countryCode: string;
  preferredLanguage: string;
  idType: IdType;
}

interface FormErrors {
  nationalId?: string;
  email?: string;
  countryCode?: string;
  preferredLanguage?: string;
  idType?: string;
}

const normalizeNationalId = (nationalId: string): string => {
  if (!nationalId || !nationalId.trim()) {
    return '';
  }
  
  // Remove any spaces
  nationalId = nationalId.trim().replace(/\s/g, '');
  
  // If no dashes, return as-is if it's all digits
  if (!nationalId.includes('-')) {
    // Personal IDs must be exactly 9 digits, Company IDs must be exactly 10 digits starting with 3
    if (/^\d{9}$/.test(nationalId) && !nationalId.startsWith('3')) {
      return nationalId; // Valid 9-digit personal ID
    }
    if (/^3\d{9}$/.test(nationalId)) {
      return nationalId; // Valid 10-digit company ID
    }
    return '';
  }
  
  // Handle formatted IDs with dashes
  const parts = nationalId.split('-');
  
  // Personal ID format: X-XXXX-XXXX (3 parts)
  if (parts.length === 3) {
    if (parts[0].length >= 1 && parts[0].length <= 2 && 
        parts[1].length >= 1 && parts[1].length <= 4 && 
        parts[2].length >= 1 && parts[2].length <= 4 &&
        parts.every(p => /^\d+$/.test(p))) {
      // Personal ID: pad middle parts to 4 digits
      const result = parts[0] + parts[1].padStart(4, '0') + parts[2].padStart(4, '0');
      // Personal IDs must be exactly 9 digits and not start with 3
      if (result.length === 9 && !result.startsWith('3')) {
        return result;
      }
      return '';
    }
  }
  
  // Company ID format: X-XXX-XXXXXX (3 parts, starts with 3)
  if (parts.length === 3 && parts[0] === '3') {
    if (parts[1].length >= 1 && parts[1].length <= 3 && 
        parts[2].length >= 1 && parts[2].length <= 6 &&
        parts.every(p => /^\d+$/.test(p))) {
      // Company ID: pad middle part to 3 digits, last part to 6 digits
      return parts[0] + parts[1].padStart(3, '0') + parts[2].padStart(6, '0');
    }
  }
  
  return ''; // Invalid format
};

const Register: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getWebPageData } = useStructuredData();
  const { executeRecaptcha } = useRecaptcha();
  const [formData, setFormData] = useState<FormData>({
    nationalId: '',
    email: '',
    countryCode: 'CR',
    preferredLanguage: DEFAULT_LANGUAGE,
    idType: IdType.PersonalId
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const pageTitle = i18n.language === 'en' 
    ? 'Register for Legal Alerts' 
    : 'Registrarse para Alertas Legales';
  const pageDescription = i18n.language === 'en'
    ? 'Sign up to receive email notifications when your name or national ID appears in official legal publications in Costa Rica.'
    : 'Regístrate para recibir notificaciones por correo cuando tu nombre o cédula aparezca en publicaciones legales oficiales de Costa Rica.';

  const structuredData = getWebPageData(pageTitle, pageDescription, t('routes.register'));

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.nationalId.trim()) {
      newErrors.nationalId = t('validation.cedulaRequired');
    } else {
      const normalizedId = normalizeNationalId(formData.nationalId.trim());
      if (!normalizedId) {
        newErrors.nationalId = t('validation.cedulaInvalid');
      } else if (formData.idType === IdType.PersonalId) {
        if (!/^\d{9}$/.test(normalizedId) || normalizedId.startsWith('3')) {
          newErrors.nationalId = t('validation.cedulaPersonalInvalid');
        }
      } else if (formData.idType === IdType.CompanyId) {
        if (!/^3\d{9}$/.test(normalizedId)) {
          newErrors.nationalId = t('validation.cedulaCompanyInvalid');
        }
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = t('validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('validation.emailInvalid');
    }

    if (!formData.countryCode) {
      newErrors.countryCode = t('validation.countryRequired');
    }

    if (!formData.preferredLanguage) {
      newErrors.preferredLanguage = t('validation.languageRequired');
    }

    if (formData.idType === undefined || formData.idType === null) {
      newErrors.idType = t('validation.idTypeRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSelectChange = (field: keyof FormData) => (event: any) => {
    const value = event.target.value as string;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts selecting
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha('register');

      await authService.register({
        nationalId: formData.nationalId.trim(),
        email: formData.email.trim(),
        countryCode: formData.countryCode,
        preferredLanguage: formData.preferredLanguage,
        idType: formData.idType,
        recaptchaToken
      });

      // Navigate to language-prefixed registration success route
      const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
      const successRoute = t('routes.registrationSuccess');
      navigate(`/${currentLanguage}${successRoute}`);
    } catch (error) {
      if (error instanceof ApiError) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(t('messages.registrationError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOErrorBoundary fallbackTitle="Register for Legal Alerts - AlertaLegal" fallbackDescription="Sign up to receive email notifications when your name or national ID appears in official legal publications in Costa Rica.">
        <SEO 
          title={pageTitle}
          description={pageDescription}
          structuredData={structuredData}
          hreflangEnabled={true}
        />
      </SEOErrorBoundary>
      <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center">
          {t('register.title')}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
          {t('register.description')}
        </Typography>

        <Alert 
          severity="success" 
          variant="filled"
          sx={{ 
            mb: 3, 
            maxWidth: 500,
            mx: 'auto',
            textAlign: 'center',
            fontSize: '1.1rem',
            fontWeight: 600,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <AlertTitle sx={{ fontSize: '1.2rem', mb: 1 }}>
            {t('register.freeService.freeForever')}
          </AlertTitle>
          {t('register.freeService.freeServiceBanner')}
        </Alert>

        <Card sx={{ maxWidth: 500, mx: 'auto' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('register.instruction')}
            </Typography>

            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <FormControl fullWidth margin="normal" error={!!errors.countryCode} required>
                <InputLabel>{t('registration.countryLabel')}</InputLabel>
                <Select
                  value={formData.countryCode}
                  onChange={handleSelectChange('countryCode')}
                  label={t('registration.countryLabel')}
                >
                  <MenuItem value="CR">{t('countries.CR')}</MenuItem>
                </Select>
                {errors.countryCode && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {errors.countryCode}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth margin="normal" error={!!errors.idType} required>
                <InputLabel>{t('registration.idTypeLabel')}</InputLabel>
                <Select
                  value={formData.idType}
                  onChange={handleSelectChange('idType')}
                  label={t('registration.idTypeLabel')}
                >
                  <MenuItem value={IdType.PersonalId}>{t('registration.personalId')}</MenuItem>
                  <MenuItem value={IdType.CompanyId}>{t('registration.companyId')}</MenuItem>
                </Select>
                {errors.idType && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {errors.idType}
                  </Typography>
                )}
              </FormControl>

              {formData.idType === IdType.CompanyId && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('register.companyIdWarning')}
                </Alert>
              )}

              <TextField
                fullWidth
                label={t('register.cedulaLabel')}
                placeholder={formData.idType === IdType.PersonalId ? t('register.cedulaPersonalPlaceholder') : t('register.cedulaCompanyPlaceholder')}
                value={formData.nationalId}
                onChange={handleInputChange('nationalId')}
                error={!!errors.nationalId}
                helperText={errors.nationalId}
                margin="normal"
                required
                slotProps={{ htmlInput: { maxLength: 12 } }}
              />

              <TextField
                fullWidth
                label={t('register.emailLabel')}
                placeholder={t('register.emailPlaceholder')}
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                margin="normal"
                required
              />

              <FormControl fullWidth margin="normal" error={!!errors.preferredLanguage} required>
                <InputLabel>{t('registration.languageLabel')}</InputLabel>
                <Select
                  value={formData.preferredLanguage}
                  onChange={handleSelectChange('preferredLanguage')}
                  label={t('registration.languageLabel')}
                >
                  <MenuItem value="es">{t('languages.es')}</MenuItem>
                  <MenuItem value="en">{t('languages.en')}</MenuItem>
                </Select>
                {errors.preferredLanguage && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {errors.preferredLanguage}
                  </Typography>
                )}
              </FormControl>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ mt: 3, mb: 2 }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    {t('messages.loading')}
                  </>
                ) : (
                  t('register.submitButton')
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
    </>
  );
};

export default Register;